import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import inventoryService from '../../services/inventoryService';

interface Lot {
  lotNumber: string;
  quantity: number;
  manufacturingDate: string;
  expiryDate?: string;
  supplier?: string;
  batchNumber?: string;
  isActive: boolean;
  additionalInfo?: Record<string, any>;
}

interface LotManagementProps {
  inventoryId: string;
  inventoryName: string;
  enableLotTracking: boolean;
}

const LotManagement: React.FC<LotManagementProps> = ({
  inventoryId,
  inventoryName,
  enableLotTracking,
}) => {
  const [lots, setLots] = useState<Lot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newLot, setNewLot] = useState({
    lotNumber: '',
    quantity: 0,
    manufacturingDate: '',
    expiryDate: '',
    supplier: '',
    batchNumber: '',
  });

  useEffect(() => {
    if (enableLotTracking) {
      fetchLots();
    }
  }, [inventoryId, enableLotTracking]);

  const fetchLots = async () => {
    try {
      setLoading(true);
      const data = await inventoryService.getLots(inventoryId, true);
      setLots(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch lots');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await inventoryService.addLot(inventoryId, newLot);
      setSuccess('Lot added successfully!');
      setShowAddModal(false);
      setNewLot({
        lotNumber: '',
        quantity: 0,
        manufacturingDate: '',
        expiryDate: '',
        supplier: '',
        batchNumber: '',
      });
      await fetchLots();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add lot');
    }
  };

  const handleDeactivateLot = async (lotNumber: string) => {
    if (!window.confirm(`Are you sure you want to deactivate lot ${lotNumber}?`)) {
      return;
    }

    try {
      await inventoryService.deactivateLot(inventoryId, lotNumber);
      setSuccess('Lot deactivated successfully!');
      await fetchLots();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate lot');
    }
  };

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  if (!enableLotTracking) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          Lot tracking is not enabled for this inventory item.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Lot Management</h3>
          <p className="text-sm text-gray-600">Track and manage inventory lots for {inventoryName}</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Lot
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-600">{success}</p>
        </div>
      )}

      {/* Lots Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : lots.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No lots added yet. Click "Add Lot" to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lot Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mfg Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch No.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lots.map((lot) => (
                  <tr key={lot.lotNumber} className={!lot.isActive ? 'bg-gray-50 opacity-60' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {lot.lotNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{lot.quantity}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(lot.manufacturingDate).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lot.expiryDate ? (
                        <div className="flex items-center">
                          {new Date(lot.expiryDate).toLocaleDateString()}
                          {isExpired(lot.expiryDate) && (
                            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 ml-2" title="Expired" />
                          )}
                          {isExpiringSoon(lot.expiryDate) && (
                            <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 ml-2" title="Expiring soon" />
                          )}
                        </div>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lot.batchNumber || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {lot.supplier || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {lot.isActive ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {lot.isActive && (
                        <button
                          onClick={() => handleDeactivateLot(lot.lotNumber)}
                          className="text-red-600 hover:text-red-900"
                          title="Deactivate lot"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Lot Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Lot</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleAddLot} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lot Number *
                  </label>
                  <input
                    type="text"
                    required
                    value={newLot.lotNumber}
                    onChange={(e) => setNewLot({ ...newLot, lotNumber: e.target.value })}
                    placeholder="LOT2024001"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={newLot.quantity}
                    onChange={(e) => setNewLot({ ...newLot, quantity: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manufacturing Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={newLot.manufacturingDate}
                    onChange={(e) => setNewLot({ ...newLot, manufacturingDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={newLot.expiryDate}
                    onChange={(e) => setNewLot({ ...newLot, expiryDate: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Batch Number
                  </label>
                  <input
                    type="text"
                    value={newLot.batchNumber}
                    onChange={(e) => setNewLot({ ...newLot, batchNumber: e.target.value })}
                    placeholder="BATCH-2024-001"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Supplier
                  </label>
                  <input
                    type="text"
                    value={newLot.supplier}
                    onChange={(e) => setNewLot({ ...newLot, supplier: e.target.value })}
                    placeholder="Supplier name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Add Lot
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LotManagement;
